import { createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { Badge, Button, SimpleGrid, Stack, Tabs, Text } from '@mantine/core';
import {
  IconChecklist, IconCircleCheck, IconCircleDashed, IconLayoutKanban, IconListCheck, IconPlus, IconProgress,
  IconTable, IconTimeline,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ResourceListPage } from '@/shared/components/ResourceListPage';
import { PageHeader } from '@/shared/components/PageHeader';
import { StatCard } from '@/shared/components/StatCard';
import { NewTaskModal } from '@/features/tasks/NewTaskModal';
import { TaskDetailsDrawer } from '@/features/tasks/TaskDetailsDrawer';
import { fetchList } from '@/shared/api/jsonapi/client';
import { statusColor, priorityColor } from '@/shared/lib/statusColors';
import { formatDate, formatNumber } from '@/shared/lib/format';
import type { FilterClause } from '@/shared/api/jsonapi/types';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

interface Task extends Denormalized {
  type: 'tasks';
  taskNumber?: number;
  title?: string;
  status?: string;
  priority?: string;
  competenceDate?: string;
}

const STATUS_LABEL: Record<string, string> = {
  backlog: 'Backlog', new: 'Nuovo', in_progress: 'In corso', interrupted: 'Interrotto',
  completed: 'Completato', cancelled: 'Annullato',
};
const PRIORITY_LABEL: Record<string, string> = {
  low: 'Bassa', medium: 'Media', high: 'Alta', urgent: 'Urgente',
};

export const Route = createFileRoute('/_app/tasks')({ component: TasksLayout });

const TABS = [
  { value: 'list', label: 'Lista', path: '/tasks', icon: IconTable },
  { value: 'kanban', label: 'Kanban', path: '/tasks/kanban', icon: IconLayoutKanban },
  { value: 'gantt', label: 'Gantt', path: '/tasks/gantt', icon: IconTimeline },
] as const;

function TasksLayout() {
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const active =
    TABS.find((t) => (t.path === '/tasks' ? path === '/tasks' : path.startsWith(t.path)))?.value ?? 'list';

  const [newOpen, setNewOpen] = useState(false);
  const [drawerTaskId, setDrawerTaskId] = useState<string | null>(null);

  return (
    <Stack gap="lg">
      <PageHeader
        title="Task"
        description="Lista, kanban e timeline delle attività."
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={() => setNewOpen(true)}>
            Nuovo task
          </Button>
        }
      />

      {active === 'list' && <TasksKpis />}

      <Tabs
        value={active}
        onChange={(v) => {
          const tab = TABS.find((t) => t.value === v);
          if (tab) void navigate({ to: tab.path });
        }}
      >
        <Tabs.List>
          {TABS.map((t) => (
            <Tabs.Tab key={t.value} value={t.value} leftSection={<t.icon size={16} />}>
              {t.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>
      {active === 'list' ? (
        <ResourceListPage<Task>
          title=""
          type="tasks"
          columns={[
            { accessorKey: 'taskNumber', header: '#', size: 80 },
            { accessorKey: 'title', header: 'Titolo' },
            {
              accessorKey: 'status',
              header: 'Stato',
              size: 130,
              Cell: ({ cell }) => {
                const s = cell.getValue<string>();
                return s ? <Badge color={statusColor(s)} variant="light">{STATUS_LABEL[s] ?? s}</Badge> : '—';
              },
            },
            {
              accessorKey: 'priority',
              header: 'Priorità',
              size: 120,
              Cell: ({ cell }) => {
                const p = cell.getValue<string>();
                return p ? <Badge color={priorityColor(p)} variant="light">{PRIORITY_LABEL[p] ?? p}</Badge> : '—';
              },
            },
            {
              accessorKey: 'competenceDate',
              header: 'Competenza',
              size: 130,
              Cell: ({ cell }) => <Text size="sm">{formatDate(cell.getValue<string>())}</Text>,
            },
          ]}
          searchField="title"
          onRowClick={(t) => setDrawerTaskId(t.id)}
          emptyMessage="Nessun task"
          emptyDescription="Crea il primo task con il bottone «Nuovo task» in alto a destra."
          emptyIcon={IconChecklist}
        />
      ) : (
        <Outlet />
      )}

      <NewTaskModal
        opened={newOpen}
        onClose={() => setNewOpen(false)}
        invalidateKeys={[['tasks'], ['tasks-kanban'], ['tasks-gantt']]}
      />
      <TaskDetailsDrawer
        taskId={drawerTaskId}
        onClose={() => setDrawerTaskId(null)}
        invalidateKeys={[['tasks'], ['tasks-kanban'], ['tasks-gantt']]}
      />
    </Stack>
  );
}

/** Conteggi globali per stato (page.size:1 → meta.total). Resilienti: in errore mostrano "—". */
function useTaskCount(key: string, filter?: Record<string, FilterClause>) {
  return useQuery({
    queryKey: ['tasks-kpi', key],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await fetchList<Task>('tasks', { page: { size: 1 }, filter });
      return res.total;
    },
  });
}

function TasksKpis() {
  const total = useTaskCount('total');
  const created = useTaskCount('new', { status: { eq: 'new' } });
  const inProgress = useTaskCount('in_progress', { status: { eq: 'in_progress' } });
  const completed = useTaskCount('completed', { status: { eq: 'completed' } });

  const fmt = (q: { data?: number }) => formatNumber(q.data ?? 0);

  return (
    <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
      <StatCard label="Totale task" icon={IconListCheck} color="violet" value={fmt(total)} loading={total.isLoading} error={total.isError} />
      <StatCard label="Nuovi" icon={IconCircleDashed} color="gray" value={fmt(created)} loading={created.isLoading} error={created.isError} />
      <StatCard label="In corso" icon={IconProgress} color="blue" value={fmt(inProgress)} loading={inProgress.isLoading} error={inProgress.isError} />
      <StatCard label="Completati" icon={IconCircleCheck} color="green" value={fmt(completed)} loading={completed.isLoading} error={completed.isError} />
    </SimpleGrid>
  );
}
